import * as React from "react";

export interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
  onError?: (error: unknown, info: { componentStack?: string | null }) => void;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: unknown;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // Log to console; hook for external logging via onError
    // Avoid throwing to keep the app running with fallback UI

    console.error("UI ErrorBoundary caught an error:", error, info);
    if (this.props.onError) {
      this.props.onError(error, { componentStack: info.componentStack });
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: undefined });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return <>{this.props.fallback}</>;
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="text-2xl font-semibold text-gray-900 mb-2">Something went wrong</div>
            <p className="text-sm text-gray-600 mb-4">
              An unexpected error occurred. You can try again.
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children as React.ReactElement;
  }
}
